import jstz from 'jstz';
import {
  createGRPCAction,
  createRequestAction,
  createRequestCancellationAction,
} from '@fullstory/redux-util';
import { UserDetailsResponse } from '@fullstory/client-api';
import { SearchieRequestTypes, SearchieActionTypes } from './types';
import { createAction, ActionType } from '@fullstory/redux-util/utils/types';
import { createAppCacheActions } from '@fullstory/redux-util/utils';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import {
  GenerateSearchieRequestProto,
  GenerateSearchieResponseProto,
  SearchieDailyActivesSettings,
  SearchieHandle,
  SearchieMobileDevicesSettings,
  SearchiePageCardSettings,
  SearchieSegmentHealthSettings,
  SearchieTopBadClicksSettings,
  SearchieType,
  SearchieUserTrendsCardSettings,
  SearchServicePublic as svc,
} from '@fullstory/proto/fs/services/alexa';
import { isDataFreshProto, optRefresh, similarData } from '@fullstory/searchiecache';
import { hashObject } from '@fullstory/util/hash';
import { SearchCacheKey } from '../searchMeta';
import { alexaGrpcTimeoutHeader } from '../searchSessions';
import { AppDispatch } from '../../types';
import { createAppCacheRequestAction } from '@fullstory/redux-util/utils/appCache/actions';
import { RequestType } from '@fullstory/util/xhr';
import { UID } from '@fullstory/util/uid';

export const SEARCHIE_REQUEST_BATCH_SIZE = 3;

export const SearchieActions = {
  ...createAppCacheActions(
    SearchieRequestTypes.SearchieAppCacheIsLoading,
    SearchieRequestTypes.SearchieAppCacheLoadFailed,
    SearchieRequestTypes.SearchieAppCacheRefetching,
    SearchieRequestTypes.SearchieAppCacheHasData,
    SearchieRequestTypes.SearchieAppCacheFresh,
    SearchieRequestTypes.SearchieAppCacheSwap,
    SearchieRequestTypes.SearchieAppCacheClear,
  )<GenerateSearchieResponseProto>(),
  userTrendsSettingsChange: (settings: SearchieDailyActivesSettings) =>
    createAction({
      type: SearchieActionTypes.UserTrendsSettingsChange,
      settings,
    }),
  rageClicksSettingsChange: (settings: SearchieTopBadClicksSettings) =>
    createAction({
      type: SearchieActionTypes.RageClicksSettingsChange,
      settings,
    }),
  mobileDeviceSettingsChange: (settings: SearchieMobileDevicesSettings) =>
    createAction({
      type: SearchieActionTypes.MobileDeviceSettingsChange,
      settings,
    }),
  deadClicksSettingsChange: (settings: SearchieTopBadClicksSettings) =>
    createAction({
      type: SearchieActionTypes.DeadClicksSettingsChange,
      settings,
    }),
  errorClicksSettingsChange: (settings: SearchieTopBadClicksSettings) =>
    createAction({
      type: SearchieActionTypes.ErrorClicksSettingsChange,
      settings,
    }),
};

export type SearchieActions = ActionType<typeof SearchieActions>;

interface SearchieRequestOptions {
  refresh?: boolean;
  settings?: unknown;
  skipRequestCache?: boolean;
  batchId: string;
}

export interface SearchiePayload {
  segmentDefinition: SegmentDefinition;
  searchCacheKey: SearchCacheKey;
  orgId: string;
  body: SearchieRequestOptions;
}

export const getSearchieBatchId = (segmentDefintion: SegmentDefinition) =>
  hashObject(segmentDefintion).toString();

export const SearchieAsyncActions = {
  appCache: (searchieType: SearchieType, uid?: UID) => ({
    action: createAppCacheRequestAction<GenerateSearchieResponseProto, SearchiePayload>()({
      query: (dispatch: AppDispatch, payload: SearchiePayload, isRefetch: boolean) =>
        dispatch(
          appCacheRequest(
            searchieType,
            payload.segmentDefinition,
            payload.searchCacheKey,
            payload.orgId,
            {
              settings: payload.body.settings,
              refresh: isRefetch,
              batchId: payload.body.batchId,
            },
            uid,
          ),
        ),
      loading: SearchieActions.appCacheIsLoading,
      failed: SearchieActions.appCacheLoadFailed,
      refetching: SearchieActions.appCacheIsRefetching,
      hasData: SearchieActions.appCacheHasData,
      fresh: SearchieActions.appCacheFresh,
      isDataFresh: (payload: SearchiePayload, data: GenerateSearchieResponseProto) =>
        isDataFreshProto(data.cacheOptions, data.computedAt, new Date()),
      isSimiliar: (
        newData: GenerateSearchieResponseProto,
        oldData: GenerateSearchieResponseProto,
      ) => similarData(searchieType, newData?.generatedSearchie, oldData?.generatedSearchie),
    }),
    cancel: (dispatch: AppDispatch, id: UID) => {
      dispatch(createRequestCancellationAction(id));
    },
  }),
};

const appCacheRequest = (
  type: SearchieType,
  segmentDefinition: SegmentDefinition,
  searchCacheKey: SearchCacheKey,
  orgId: string,
  {
    settings,
    refresh = false,
    batchId = hashObject(segmentDefinition).toString(),
  }: SearchieRequestOptions,
  uid?: UID,
) => {
  const cacheOptions = refresh ? optRefresh : null;
  const body: GenerateSearchieRequestProto = {
    orgId,
    handle: createHandle(segmentDefinition, type, settings),
    timezone: jstz.determine().name(),
    now: null,
    granularization: null,
    cacheOptions,
    sampleOptions: null,
    followUp: refresh,
  };

  return createGRPCAction<typeof svc.GenerateSearchie>({
    cacheKey: (_: string) => `${hashObject(body.handle)}:${hashObject(searchCacheKey)}`,
    uid,
  })(
    {
      type: SearchieRequestTypes.AppCacheRequest,
      grpcFunction: svc.GenerateSearchie,
      body,
      readWrite: 'write',
      settings: {
        batchOptions: {
          maxOutgoingRequests: SEARCHIE_REQUEST_BATCH_SIZE,
          id: batchId,
        },
        headers: alexaGrpcTimeoutHeader,
      },
      force: refresh,
    },
    SearchieRequestTypes.AppCacheSuccess,
    SearchieRequestTypes.AppCacheFailure,
  );
};

const createHandle = (
  segmentDefinition: SegmentDefinition,
  type: SearchieType,
  settings: unknown,
): SearchieHandle => {
  const handle = { type, segmentDefinition } as SearchieHandle;
  switch (type) {
    case SearchieType.SEARCHIE_TYPE_TOP_ERROR_CLICKS:
      handle.topErrorClicksSettings = settings as SearchieTopBadClicksSettings;
      break;
    case SearchieType.SEARCHIE_TYPE_TOP_RAGE_CLICKS:
      handle.topRageClicksSettings = settings as SearchieTopBadClicksSettings;
      break;
    case SearchieType.SEARCHIE_TYPE_TOP_DEAD_CLICKS:
      handle.topDeadClicksSettings = settings as SearchieTopBadClicksSettings;
      break;
    case SearchieType.SEARCHIE_TYPE_SEGMENT_HEALTH:
      handle.segmentHealthSettings = settings as SearchieSegmentHealthSettings;
      break;
    case SearchieType.SEARCHIE_TYPE_DAILY_ACTIVES:
      handle.dailyActivesSettings = settings as SearchieDailyActivesSettings;
      break;
    case SearchieType.SEARCHIE_TYPE_MOBILE_DEVICES:
      handle.mobileDevicesSettings = settings as SearchieMobileDevicesSettings;
      break;
    case SearchieType.SEARCHIE_TYPE_USER_TRENDS_CARD:
      handle.userTrendsCardSettings = settings as SearchieUserTrendsCardSettings;
      break;
    case SearchieType.SEARCHIE_TYPE_TOP_PAGES:
      handle.topPagesSettings = settings as SearchiePageCardSettings;
      break;
    case SearchieType.SEARCHIE_TYPE_SLOW_PAGES:
      handle.slowPagesSettings = settings as SearchiePageCardSettings;
      break;
    case SearchieType.SEARCHIE_TYPE_MOST_COMMON_ENTRY_PAGES:
      handle.mostCommonEntryPagesSettings = settings as SearchiePageCardSettings;
      break;
  }
  return handle;
};

export const UserRequestActions = {
  userDetailsRequest: (userID: string, segmentDefinition: SegmentDefinition) =>
    createRequestAction<UserDetailsResponse>()(
      {
        type: SearchieRequestTypes.UserDetailsRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          headers: {
            'X-Use-Segment-Definition': '1',
          },
          url: '/userDetails',
          params: { Tz: jstz.determine().name(), IndvId: userID },
        },
        body: segmentDefinition,
        readWrite: 'write',
      },
      SearchieRequestTypes.UserDetailsRequestSuccess,
      SearchieRequestTypes.UserDetailsRequestFailure,
    ),
};

export const SearchieRequests = {
  searchieSuggestionRequest: (useCase: string, roleId: string) =>
    createRequestAction<any>()(
      {
        type: SearchieRequestTypes.SearchieSuggestionRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: '/searchie/suggestion',
          params: { tz: jstz.determine().name() },
        },
        readWrite: 'write',
        body: {
          UseCase: useCase,
          Role: roleId,
        },
      },
      SearchieRequestTypes.SearchieSuggestionSuccess,
      SearchieRequestTypes.SearchieSuggestionFailure,
    ),
  generateSearchie: (body: GenerateSearchieRequestProto, force?: boolean) =>
    createGRPCAction<typeof svc.GenerateSearchie>()(
      {
        type: SearchieRequestTypes.GenerateSearchieRequest,
        grpcFunction: svc.GenerateSearchie,
        body,
        force,
        readWrite: 'read',
      },
      SearchieRequestTypes.GenerateSearchieSuccess,
      SearchieRequestTypes.GenerateSearchieFailure,
    ),
};
